import { cloneDeep } from 'lodash'
import { ethAddress, type Address } from 'viem'
import { getAddress } from 'viem'
import type { Config } from 'wagmi'
import type { StoreApi } from 'zustand'
import type {
  FormLpTokenExpected,
  FormStatus,
  FormType,
  FormValues,
  LoadMaxAmount,
} from '@/dex/components/PagePool/Deposit/types'
import {
  DEFAULT_FORM_LP_TOKEN_EXPECTED,
  DEFAULT_FORM_STATUS,
  DEFAULT_FORM_VALUES,
} from '@/dex/components/PagePool/Deposit/utils'
import type { EstimatedGas as FormEstGas, Slippage } from '@/dex/components/PagePool/types'
import {
  DEFAULT_ESTIMATED_GAS,
  DEFAULT_SLIPPAGE,
  getAmountsError,
  parseAmountsForAPI,
} from '@/dex/components/PagePool/utils'
import type { Amount } from '@/dex/components/PagePool/utils'
import { curvejsApi } from '@/dex/lib/curvejs'
import type { State } from '@/dex/store/useStore'
import {
  ChainId,
  CurveApi,
  FnStepApproveResponse,
  FnStepEstGasApprovalResponse,
  FnStepResponse,
} from '@/dex/types/main.types'
import { isBonus, isHighSlippage } from '@/dex/utils'
import { getMaxAmountMinusGas } from '@/dex/utils/utilsGasPrices'
import type { PoolTemplate } from '@curvefi/api/lib/pools'
import { useWallet } from '@evm-ui/features/connect-wallet'
import { fetchTokenBalance } from '@evm-ui/hooks/useTokenBalance'
import { fetchGasInfoAndUpdateLib } from '@evm-ui/queries/gas-info.query'
import { setMissingProvider } from '@evm-ui/utils/store.util'
import type { Nullish } from '@primitives/objects.utils'
import { t } from '@ui/lib/i18n'
import { fetchPoolTokenBalances } from '../hooks/usePoolTokenBalances'
import { fetchPoolLpTokenBalance } from '../hooks/usePoolTokenDepositBalances'
import { getTokens, hasWrapped } from '../pool.utils'
import { invalidatePoolInfo, invalidateUserPoolInfo } from '../queries/invalidation'

type StateKey = keyof typeof DEFAULT_STATE

type SliceState = {
  activeKey: string
  poolAddress: string
  formEstGas: Record<string, FormEstGas>
  formLpTokenExpected: Record<string, FormLpTokenExpected>
  formStatus: FormStatus
  formValues: FormValues
  maxLoading: number | null
  slippage: Record<string, Slippage>
}

const SLICE_KEY = 'poolDeposit'

// prettier-ignore
export type PoolDepositSlice = {
  [SLICE_KEY]: SliceState & {
    fetchExpected: (activeKey: string, formType: FormType, pool: PoolTemplate, formValues: FormValues) => Promise<void>
    fetchMaxAmount: (config: Config, activeKey: string, chainId: ChainId, userAddress: Address, pool: PoolTemplate, loadMaxAmount: LoadMaxAmount, maxSlippage: string) => Promise<Amount[]>
    fetchSeedAmount: (pool: PoolTemplate, formValues: FormValues) => Promise<Pick<FormValues, 'amounts' | 'isWrapped'>>
    fetchSlippage: (activeKey: string, formType: FormType, pool: PoolTemplate, formValues: FormValues, maxSlippage: string) => Promise<void>
    setFormValues: (formType: FormType, config: Config, curve: CurveApi | null, poolId: string, pool: PoolTemplate | undefined, formValues: Partial<FormValues>, loadMaxAmount: LoadMaxAmount | null, isSeed: boolean | Nullish, maxSlippage: string) => Promise<void>

    // steps
    fetchEstGasApproval: (activeKey: string, chainId: ChainId, formType: FormType, pool: PoolTemplate, maxSlippage: string) => Promise<FnStepEstGasApprovalResponse>
    fetchStepApprove: (activeKey: string, curve: CurveApi, formType: FormType, pool: PoolTemplate, formValues: FormValues, maxSlippage: string) => Promise<FnStepApproveResponse | undefined>
    fetchStepDeposit: (activeKey: string, curve: CurveApi, pool: PoolTemplate, formValues: FormValues, maxSlippage: string) => Promise<FnStepResponse | undefined>
    fetchStepDepositStake: (activeKey: string, curve: CurveApi, pool: PoolTemplate, formValues: FormValues, maxSlippage: string) => Promise<FnStepResponse | undefined>
    fetchStepStakeApprove: (activeKey: string, curve: CurveApi, formType: FormType, pool: PoolTemplate, formValues: FormValues) => Promise<FnStepApproveResponse | undefined>
    fetchStepStake: (activeKey: string, curve: CurveApi, pool: PoolTemplate, formValues: FormValues) => Promise<FnStepResponse | undefined>

    setStateByActiveKey: <T>(key: StateKey, activeKey: string, value: T) => void
    setStateByKey: <T>(key: StateKey, value: T) => void
    setStateByKeys: (SliceState: Partial<SliceState>) => void
    resetState: (pool: PoolTemplate, isWrapped: boolean) => void
  }
}

const DEFAULT_STATE: SliceState = {
  activeKey: '',
  poolAddress: '',
  formEstGas: {},
  formLpTokenExpected: {},
  formStatus: DEFAULT_FORM_STATUS,
  formValues: DEFAULT_FORM_VALUES,
  maxLoading: null,
  slippage: {},
}

export const createPoolDepositSlice = (
  _set: StoreApi<State>['setState'],
  get: StoreApi<State>['getState'],
): PoolDepositSlice => ({
  [SLICE_KEY]: {
    ...DEFAULT_STATE,

    fetchExpected: async (activeKey, formType, pool, formValues) => {
      const { amounts, isWrapped } = formValues
      const depositExpectedFn =
        formType === 'DEPOSIT' ? curvejsApi.poolDeposit.depositExpected : curvejsApi.poolDeposit.depositAndStakeExpected

      const [fetchedExpected, fetchedParameters] = await Promise.all([
        depositExpectedFn(activeKey, pool, isWrapped, parseAmountsForAPI(amounts)),
        pool.stats.parameters(),
      ])

      get()[SLICE_KEY].setStateByKey('formLpTokenExpected', {
        [activeKey]: { ...fetchedExpected, loading: false, virtualPrice: fetchedParameters?.virtualPrice },
      })
    },
    fetchMaxAmount: async (config, activeKey, chainId, userAddress, pool, { tokenAddress, idx }, maxSlippage) => {
      const cFormValues = cloneDeep(get()[SLICE_KEY].formValues)
      const userBalance = await fetchTokenBalance(config, {
        chainId,
        userAddress,
        tokenAddress: tokenAddress as Address,
      })

      if (tokenAddress.toLowerCase() === ethAddress) {
        // set loading
        cFormValues.amounts[idx].value = ''
        get()[SLICE_KEY].setStateByKeys({ formValues: cloneDeep(cFormValues), maxLoading: idx })

        // fetch est gas
        cFormValues.amounts[idx].value = userBalance
        const resp = await curvejsApi.poolDeposit.depositEstGasApproval(
          activeKey,
          chainId,
          pool,
          cFormValues.isWrapped,
          parseAmountsForAPI(cFormValues.amounts),
          maxSlippage,
        )
        const { basePlusPriority } = await fetchGasInfoAndUpdateLib({ chainId })

        if (!resp.error && resp.estimatedGas && basePlusPriority?.[0]) {
          cFormValues.amounts[idx].value = getMaxAmountMinusGas(resp.estimatedGas, basePlusPriority[0], userBalance)
          return cFormValues.amounts
        } else {
          console.error('unable to get est gas value')
        }
      }
      cFormValues.amounts[idx].value = userBalance
      return cFormValues.amounts
    },
    fetchSeedAmount: async (pool, formValues) => {
      const { underlyingCoins, underlyingCoinAddresses, wrappedCoins, wrappedCoinAddresses } = pool

      const firstAmount = formValues.amounts[0].value
      const haveFirstAmount = Number(firstAmount) > 0
      const tokens = hasWrapped(pool) ? wrappedCoins : underlyingCoins
      const tokenAddresses = hasWrapped(pool) ? wrappedCoinAddresses : underlyingCoinAddresses

      try {
        const seedAmounts = haveFirstAmount ? await pool.getSeedAmounts(firstAmount, !hasWrapped) : null
        return {
          amounts: tokens.map((token, idx) => ({
            token,
            tokenAddress: tokenAddresses[idx],
            value: seedAmounts?.[idx] || '',
          })),
          isWrapped: hasWrapped(pool) ?? false,
        }
      } catch (error) {
        console.error('Api error getSeedAmounts', error)

        return {
          amounts: tokens.map((token, idx) => {
            const value = idx === 0 ? formValues.amounts[idx].value : ''
            return { token, tokenAddress: tokenAddresses[idx], value }
          }),
          isWrapped: hasWrapped(pool) ?? false,
        }
      }
    },
    fetchSlippage: async (activeKey, formType, pool, formValues, maxSlippage) => {
      const { amounts: cFormAmounts, isWrapped } = formValues
      const amounts = parseAmountsForAPI(cFormAmounts)
      const resp =
        formType === 'DEPOSIT'
          ? await curvejsApi.poolDeposit.depositBonus(activeKey, pool, isWrapped, amounts)
          : await curvejsApi.poolDeposit.depositAndStakeBonus(activeKey, pool, isWrapped, amounts)

      if (resp.error) {
        get()[SLICE_KEY].setStateByKeys({
          formStatus: {
            ...cloneDeep(DEFAULT_FORM_STATUS),
            isApproved: get()[SLICE_KEY].formStatus.isApproved,
            error: resp.error,
          },
          slippage: { [activeKey]: cloneDeep(DEFAULT_SLIPPAGE) },
        })
      } else {
        get()[SLICE_KEY].setStateByKey('slippage', {
          [resp.activeKey]: {
            ...DEFAULT_SLIPPAGE,
            loading: false,
            slippage: Math.abs(+resp.bonus),
            isHighSlippage: isHighSlippage(+resp.bonus, maxSlippage),
            isBonus: isBonus(+resp.bonus),
          },
        })
      }
    },
    setFormValues: async (
      formType,
      config,
      curve,
      poolId,
      pool,
      updatedFormValues,
      loadMaxAmount,
      isSeed,
      maxSlippage,
    ) => {
      // stored values
      const storedActiveKey = get()[SLICE_KEY].activeKey
      const storedFormValues = get()[SLICE_KEY].formValues
      const storedFormStatus = get()[SLICE_KEY].formStatus

      // update form values, form status, activeKey
      const cFormValues = cloneDeep({ ...storedFormValues, ...updatedFormValues })
      let activeKey = getActiveKey(poolId, formType, cFormValues, maxSlippage)
      get()[SLICE_KEY].setStateByKeys({
        activeKey,
        formStatus: { ...DEFAULT_FORM_STATUS, isApproved: storedFormStatus.isApproved },
        formValues: cloneDeep(cFormValues),
      })

      if (!curve || !pool || isSeed == null || cFormValues.isWrapped === null) return

      const { chainId, signerAddress } = curve

      if (formType === 'DEPOSIT' || formType === 'DEPOSIT_STAKE') {
        // max amount
        if (loadMaxAmount) {
          cFormValues.amounts = await get()[SLICE_KEY].fetchMaxAmount(
            config,
            activeKey,
            chainId,
            signerAddress,
            pool,
            loadMaxAmount,
            maxSlippage,
          )
          activeKey = getActiveKey(pool.id, formType, cFormValues, maxSlippage)
          get()[SLICE_KEY].setStateByKeys({ activeKey, formValues: cloneDeep(cFormValues), maxLoading: null })
        } else if (cFormValues.isBalancedAmounts === 'by-wallet') {
          // get balanced amounts
          const resp = await curvejsApi.poolDeposit.depositBalancedAmounts(activeKey, pool, cFormValues.isWrapped)

          if (resp.error) {
            get()[SLICE_KEY].setStateByKey('formStatus', { ...get()[SLICE_KEY].formStatus, error: resp.error })
          } else {
            const { tokens, tokenAddresses } = getTokens(pool, { wrapped: cFormValues.isWrapped })

            cFormValues.amounts = tokenAddresses.map((address, idx) => ({
              value: resp.amounts[idx],
              token: tokens[idx],
              tokenAddress: address,
            }))
            activeKey = getActiveKey(pool.id, formType, cFormValues, maxSlippage)
            get()[SLICE_KEY].setStateByKeys({ activeKey, formValues: cloneDeep(cFormValues) })
          }
        }

        // update amounts input based on options (Seed, MaxAmount, BalancedAmounts)
        if (isSeed) {
          // get seed amounts
          const { amounts, isWrapped } = await get()[SLICE_KEY].fetchSeedAmount(pool, cFormValues)
          cFormValues.amounts = amounts
          cFormValues.isWrapped = isWrapped
          activeKey = getActiveKey(pool.id, formType, cFormValues, maxSlippage)
          get()[SLICE_KEY].setStateByKeys({ activeKey, formValues: cloneDeep(cFormValues) })
        }

        if (cFormValues.amounts.some(a => +a.value > 0)) {
          // fetch expected LP Tokens
          get()[SLICE_KEY].setStateByActiveKey('formLpTokenExpected', activeKey, {
            ...(get()[SLICE_KEY].formLpTokenExpected[storedActiveKey] ?? DEFAULT_FORM_LP_TOKEN_EXPECTED),
            loading: true,
          })
          void get()[SLICE_KEY].fetchExpected(activeKey, formType, pool, cFormValues)

          if (!isSeed) {
            // fetch slippage
            get()[SLICE_KEY].setStateByActiveKey('slippage', activeKey, {
              ...(get()[SLICE_KEY].slippage[storedActiveKey] ?? DEFAULT_SLIPPAGE),
              loading: true,
            })
            void get()[SLICE_KEY].fetchSlippage(activeKey, formType, pool, cFormValues, maxSlippage)
          }

          if (signerAddress) {
            // validate input amounts with wallet
            const balances = await fetchPoolTokenBalances(config, curve, getAddress(pool.address))
            const amountsError = getAmountsError(cFormValues.amounts, balances)

            if (amountsError) {
              get()[SLICE_KEY].setStateByKey('formStatus', {
                ...get()[SLICE_KEY].formStatus,
                error: t`Not enough balance for ${amountsError}.`,
              })
            } else {
              // get est gas and approval
              get()[SLICE_KEY].setStateByActiveKey('formEstGas', activeKey, {
                ...(get()[SLICE_KEY].formEstGas[storedActiveKey] ?? DEFAULT_ESTIMATED_GAS),
                loading: true,
              })
              void get()[SLICE_KEY].fetchEstGasApproval(activeKey, chainId, formType, pool, maxSlippage)
            }
          }
        }
      } else if (formType === 'STAKE') {
        if (!!signerAddress && +cFormValues.lpToken > 0) {
          // validate lpToken balances
          const lpTokenBalance = await fetchPoolLpTokenBalance(config, curve, getAddress(pool.address))
          const lpTokenError = +cFormValues.lpToken > +lpTokenBalance ? 'lpToken-too-much' : ''

          if (lpTokenError) {
            get()[SLICE_KEY].setStateByKey('formStatus', {
              ...cloneDeep(get()[SLICE_KEY].formStatus),
              error: lpTokenError,
            })
          } else {
            // get est gas and approval
            get()[SLICE_KEY].setStateByActiveKey('formEstGas', activeKey, {
              ...(get()[SLICE_KEY].formEstGas[storedActiveKey] ?? DEFAULT_ESTIMATED_GAS),
              loading: true,
            })
            void get()[SLICE_KEY].fetchEstGasApproval(activeKey, chainId, formType, pool, maxSlippage)
          }
        }
      }
    },

    // steps
    fetchEstGasApproval: async (activeKey, chainId, formType, pool, maxSlippage) => {
      const cFormValues = cloneDeep(get()[SLICE_KEY].formValues)
      const { amounts, isWrapped, lpToken } = cFormValues
      const resp =
        formType === 'DEPOSIT'
          ? await curvejsApi.poolDeposit.depositEstGasApproval(
              activeKey,
              chainId,
              pool,
              isWrapped,
              parseAmountsForAPI(amounts),
              maxSlippage,
            )
          : formType === 'DEPOSIT_STAKE'
            ? await curvejsApi.poolDeposit.depositAndStakeEstGasApproval(
                activeKey,
                chainId,
                pool,
                isWrapped,
                parseAmountsForAPI(amounts),
                maxSlippage,
              )
            : await curvejsApi.poolDeposit.stakeEstGasApproval(activeKey, chainId, pool, lpToken)

      // set estimate gas state
      get()[SLICE_KEY].setStateByActiveKey('formEstGas', activeKey, { estimatedGas: resp.estimatedGas, loading: false })

      // set form status
      const storedFormStatus = get()[SLICE_KEY].formStatus
      if (!storedFormStatus.formProcessing) {
        get()[SLICE_KEY].setStateByKey('formStatus', {
          ...storedFormStatus,
          isApproved: resp.isApproved,
          error: storedFormStatus.error || resp.error,
        })
      }
      return resp
    },
    fetchStepApprove: async (activeKey, curve, formType, pool, formValues, maxSlippage) => {
      const { provider } = useWallet.getState()
      if (!provider) return setMissingProvider(get()[SLICE_KEY])

      get()[SLICE_KEY].setStateByKey('formStatus', {
        ...get()[SLICE_KEY].formStatus,
        formProcessing: true,
        step: 'APPROVAL',
      })
      const { chainId } = curve
      const { amounts, isWrapped } = formValues
      const approveFn =
        formType === 'DEPOSIT' ? curvejsApi.poolDeposit.depositApprove : curvejsApi.poolDeposit.depositAndStakeApprove
      const resp = await approveFn(activeKey, provider, pool, isWrapped, parseAmountsForAPI(amounts))
      if (resp.activeKey === get()[SLICE_KEY].activeKey) {
        const cFormStatus = cloneDeep(get()[SLICE_KEY].formStatus)
        cFormStatus.formProcessing = false
        cFormStatus.step = ''
        cFormStatus.error = ''

        if (resp.error) {
          cFormStatus.error = resp.error
          get()[SLICE_KEY].setStateByKey('formStatus', cFormStatus)
        } else {
          cFormStatus.formTypeCompleted = 'APPROVE'
          cFormStatus.isApproved = true
          get()[SLICE_KEY].setStateByKey('formStatus', cFormStatus)

          // fetch est gas and approval
          await get()[SLICE_KEY].fetchEstGasApproval(activeKey, chainId, formType, pool, maxSlippage)
        }

        return resp
      }
    },
    fetchStepDeposit: async (activeKey, curve, pool, formValues, maxSlippage) => {
      const { provider } = useWallet.getState()
      if (!provider) return setMissingProvider(get()[SLICE_KEY])

      get()[SLICE_KEY].setStateByKey('formStatus', {
        ...get()[SLICE_KEY].formStatus,
        formProcessing: true,
        step: 'DEPOSIT',
      })

      const { amounts, isWrapped } = formValues
      const resp = await curvejsApi.poolDeposit.deposit(
        activeKey,
        provider,
        pool,
        isWrapped,
        parseAmountsForAPI(amounts),
        maxSlippage,
      )

      if (resp.activeKey === get()[SLICE_KEY].activeKey) {
        const cFormStatus = cloneDeep(get()[SLICE_KEY].formStatus)
        cFormStatus.formProcessing = false
        cFormStatus.step = ''
        cFormStatus.error = ''

        if (resp.error) {
          cFormStatus.error = resp.error
          get()[SLICE_KEY].setStateByKey('formStatus', cFormStatus)
        } else {
          cFormStatus.formTypeCompleted = 'DEPOSIT'
          get()[SLICE_KEY].setStateByKeys({ formStatus: cFormStatus, formValues: resetFormValues(formValues) })

          const params = {
            chainId: curve.chainId,
            poolAddress: getAddress(pool.address),
            userAddress: curve.signerAddress,
          }
          await Promise.all([invalidateUserPoolInfo(params), invalidatePoolInfo(params)])
        }

        return resp
      }
    },
    fetchStepDepositStake: async (activeKey, curve, pool, formValues, maxSlippage) => {
      const { provider } = useWallet.getState()
      if (!provider) return setMissingProvider(get()[SLICE_KEY])

      get()[SLICE_KEY].setStateByKey('formStatus', {
        ...get()[SLICE_KEY].formStatus,
        formProcessing: true,
        step: 'DEPOSIT_STAKE',
      })
      const { amounts, isWrapped } = formValues
      const resp = await curvejsApi.poolDeposit.depositAndStake(
        activeKey,
        provider,
        pool,
        isWrapped,
        parseAmountsForAPI(amounts),
        maxSlippage,
      )
      if (resp.activeKey === get()[SLICE_KEY].activeKey) {
        const cFormStatus = cloneDeep(get()[SLICE_KEY].formStatus)
        cFormStatus.formProcessing = false
        cFormStatus.step = ''
        cFormStatus.error = ''

        if (resp.error) {
          cFormStatus.error = resp.error
          get()[SLICE_KEY].setStateByKey('formStatus', cFormStatus)
        } else {
          cFormStatus.formTypeCompleted = 'DEPOSIT_STAKE'
          get()[SLICE_KEY].setStateByKeys({ formStatus: cFormStatus, formValues: resetFormValues(formValues) })

          const params = {
            chainId: curve.chainId,
            poolAddress: getAddress(pool.address),
            userAddress: curve.signerAddress,
          }
          await Promise.all([invalidateUserPoolInfo(params), invalidatePoolInfo(params)])
        }

        return resp
      }
    },
    fetchStepStakeApprove: async (activeKey, curve, formType, pool, formValues) => {
      const { provider } = useWallet.getState()
      if (!provider) return setMissingProvider(get()[SLICE_KEY])

      get()[SLICE_KEY].setStateByKey('formStatus', {
        ...get()[SLICE_KEY].formStatus,
        formProcessing: true,
        step: 'APPROVAL',
      })
      const { chainId } = curve
      const { lpToken } = formValues
      const resp = await curvejsApi.poolDeposit.stakeApprove(activeKey, provider, pool, lpToken)
      if (resp.activeKey === get()[SLICE_KEY].activeKey) {
        const cFormStatus = cloneDeep(get()[SLICE_KEY].formStatus)
        cFormStatus.formProcessing = false
        cFormStatus.step = ''
        cFormStatus.error = ''

        if (resp.error) {
          cFormStatus.error = resp.error
          get()[SLICE_KEY].setStateByKey('formStatus', cFormStatus)
        } else {
          cFormStatus.formTypeCompleted = 'APPROVE'
          cFormStatus.isApproved = true
          get()[SLICE_KEY].setStateByKey('formStatus', cFormStatus)

          // fetch est gas and approval
          await get()[SLICE_KEY].fetchEstGasApproval(activeKey, chainId, formType, pool, '')
        }

        return resp
      }
    },
    fetchStepStake: async (activeKey, curve, pool, formValues) => {
      const { provider } = useWallet.getState()
      if (!provider) return setMissingProvider(get()[SLICE_KEY])

      get()[SLICE_KEY].setStateByKey('formStatus', {
        ...get()[SLICE_KEY].formStatus,
        formProcessing: true,
        step: 'STAKE',
      })
      const { lpToken } = formValues
      const resp = await curvejsApi.poolDeposit.stake(activeKey, provider, pool, lpToken)
      if (resp.activeKey === get()[SLICE_KEY].activeKey) {
        const cFormStatus = cloneDeep(get()[SLICE_KEY].formStatus)
        cFormStatus.formProcessing = false
        cFormStatus.step = ''
        cFormStatus.error = ''

        if (resp.error) {
          cFormStatus.error = resp.error
          get()[SLICE_KEY].setStateByKey('formStatus', cFormStatus)
        } else {
          cFormStatus.formTypeCompleted = 'STAKE'
          get()[SLICE_KEY].setStateByKeys({ formStatus: cFormStatus, formValues: resetFormValues(formValues) })

          const params = {
            chainId: curve.chainId,
            poolAddress: getAddress(pool.address),
            userAddress: curve.signerAddress,
          }
          await Promise.all([invalidateUserPoolInfo(params), invalidatePoolInfo(params)])
        }

        return resp
      }
    },

    // slice helpers
    setStateByActiveKey: (key, activeKey, value) => {
      const foundKey = get()[SLICE_KEY][key] as object
      if (Object.keys(foundKey).length > 30) {
        get().setAppStateByKey(SLICE_KEY, key, { [activeKey]: value })
      } else {
        get().setAppStateByActiveKey(SLICE_KEY, key, activeKey, value)
      }
    },
    setStateByKey: (key, value) => {
      get().setAppStateByKey(SLICE_KEY, key, value)
    },
    setStateByKeys: sliceState => {
      get().setAppStateByKeys(SLICE_KEY, sliceState)
    },
    resetState: (pool, isWrapped) => {
      const { tokens, tokenAddresses } = getTokens(pool, { wrapped: isWrapped })
      get().resetAppState(SLICE_KEY, {
        ...DEFAULT_STATE,
        formValues: {
          ...DEFAULT_FORM_VALUES,
          isWrapped,
          amounts: tokens.map((token, idx) => ({ token, tokenAddress: tokenAddresses[idx], value: '' })),
        },
      })
    },
  },
})

function getActiveKey(
  poolId: string,
  formType: FormType,
  { amounts, isBalancedAmounts, isWrapped, lpToken }: FormValues,
  maxSlippage: string,
) {
  let activeKey = `${formType}-${poolId}-`
  if (formType === 'DEPOSIT' || formType === 'DEPOSIT_STAKE') {
    const amountsStr = amounts.map(a => a.value).join('-')
    activeKey += `${amountsStr}-${isBalancedAmounts}-${isWrapped}-${maxSlippage}-${lpToken}`
  } else {
    activeKey += `${lpToken}`
  }
  return activeKey
}

const resetFormValues = (formValues: FormValues): FormValues => ({
  ...cloneDeep(formValues),
  isBalancedAmounts: false as const,
  lpToken: '',
  amounts: formValues.amounts.map(a => ({ ...a, value: '' })),
})
